import { ArrowLeft, MoreHorizontal, Pencil, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate, useMatch, useNavigate, useParams } from 'react-router-dom'
import EntityProfileLink from '../../components/watchlist/EntityProfileLink'
import WatchlistListPicker from '../../components/watchlist/WatchlistListPicker'
import { Menu, MenuItem, MenuSeparator, Modal } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import {
  deleteAdminWatchlistItem,
  deleteAdminWatchlistList,
  fetchAdminWatchlistListDetail,
  fetchAdminWatchlistUncategorized,
  renameAdminWatchlistList,
  updateAdminWatchlistItemLists,
  type AdminWatchlistListDetail,
} from '../../services/admin'
import type { WatchlistItem, WatchlistList } from '../../services/watchlist'
import { formatDateTimeDa } from '../../utils/format'

const inputClass =
  'w-full border border-rule rounded-sm bg-paper px-2 py-1 text-xs text-ink placeholder:text-ink-300 focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink/10'
const sectionClass = 'border border-rule rounded-sm p-3'

function entityProfilePath(item: WatchlistItem): string {
  return item.entityType === 'company'
    ? `/company/${item.entityId}`
    : `/person/${item.entityId}`
}

const AdminUserWatchlistListPage: React.FC = () => {
  const { userId, listId } = useParams<{ userId: string; listId?: string }>()
  const uncategorizedMatch = useMatch('/account/admin/brugere/:userId/watchlist/uncategorized')
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const { showToast } = useToast()

  const targetId = Number(userId)
  const isUncategorized = Boolean(uncategorizedMatch)
  const numericListId = listId ? Number(listId) : null

  const [detail, setDetail] = useState<AdminWatchlistListDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)

  const [editItem, setEditItem] = useState<WatchlistItem | null>(null)
  const [editListIds, setEditListIds] = useState<number[]>([])
  const [itemBusy, setItemBusy] = useState(false)

  const [showDeleteList, setShowDeleteList] = useState(false)
  const [deleteListBusy, setDeleteListBusy] = useState(false)

  const load = useCallback(async () => {
    if (!Number.isFinite(targetId) || targetId <= 0) {
      setError('Ugyldig bruger.')
      setLoading(false)
      return
    }
    if (!isUncategorized && (!Number.isFinite(numericListId) || numericListId! <= 0)) {
      setError('Ugyldig liste.')
      setLoading(false)
      return
    }
    setError(null)
    try {
      const data = isUncategorized
        ? await fetchAdminWatchlistUncategorized(targetId)
        : await fetchAdminWatchlistListDetail(targetId, numericListId!)
      setDetail(data)
      setNameDraft(data.list.name)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Kunne ikke hente liste')
    } finally {
      setLoading(false)
    }
  }, [targetId, numericListId, isUncategorized])

  useEffect(() => {
    void load()
  }, [load])

  if (!currentUser) return null
  if (!currentUser.isAdmin) return <Navigate to="/account" replace />

  async function handleRename(e: React.FormEvent) {
    e.preventDefault()
    if (!detail || isUncategorized || detail.list.id === null) return
    const name = nameDraft.trim()
    if (!name) return
    setRenameBusy(true)
    try {
      await renameAdminWatchlistList(targetId, detail.list.id, name)
      await load()
      setEditingName(false)
      showToast('Liste omdøbt.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setRenameBusy(false)
    }
  }

  async function handleDeleteList() {
    if (!detail || detail.list.id === null) return
    setDeleteListBusy(true)
    try {
      await deleteAdminWatchlistList(targetId, detail.list.id)
      showToast('Liste slettet.')
      navigate(`/account/admin/brugere/${targetId}`)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setDeleteListBusy(false)
    }
  }

  function openEditItem(item: WatchlistItem) {
    setEditItem(item)
    setEditListIds([...item.listIds])
  }

  async function saveEditItem() {
    if (!editItem) return
    setItemBusy(true)
    try {
      await updateAdminWatchlistItemLists(targetId, editItem.id, editListIds)
      setEditItem(null)
      await load()
      showToast('Emne opdateret.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setItemBusy(false)
    }
  }

  async function handleRemoveItem(item: WatchlistItem) {
    if (!window.confirm(`Fjern ${item.displayName} fra watchlist?`)) return
    try {
      await deleteAdminWatchlistItem(targetId, item.id)
      await load()
      showToast('Emne fjernet.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl')
    }
  }

  if (loading) {
    return <p className="text-sm text-ink-500">Henter liste…</p>
  }

  if (error || !detail) {
    return (
      <div>
        <BackLink userId={targetId} userName={null} />
        <p className="text-sm text-signal" role="alert">
          {error ?? 'Liste ikke fundet.'}
        </p>
      </div>
    )
  }

  const { user, list, allLists } = detail
  const pickerLists: WatchlistList[] = allLists.map((l) => ({
    ...l,
    sortOrder: 0,
    createdAt: '',
    items: [],
  }))

  return (
    <div>
      <BackLink userId={targetId} userName={user.name} />

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          {editingName && !isUncategorized ? (
            <form onSubmit={(e) => void handleRename(e)} className="flex gap-2 items-center">
              <input
                className={inputClass}
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                aria-label="Listenavn"
                required
                autoFocus
              />
              <button
                type="submit"
                disabled={renameBusy || !nameDraft.trim()}
                className="shrink-0 px-2 py-1 bg-ink text-paper text-xs rounded-sm disabled:opacity-50"
              >
                Gem
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingName(false)
                  setNameDraft(list.name)
                }}
                className="text-ink-500 hover:text-ink"
                aria-label="Annuller"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="font-serif text-2xl text-ink truncate" title={list.name}>
                {list.name}
              </h1>
              {!isUncategorized ? (
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  className="shrink-0 text-ink-500 hover:text-ink"
                  aria-label="Omdøb liste"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          )}
          <p className="text-xs text-ink-500 mt-1">
            {isUncategorized ? (
              <>Emner uden listetilknytning · {user.name}</>
            ) : (
              <>
                Oprettet af {user.name}
                {list.createdAt ? (
                  <> · {formatDateTimeDa(list.createdAt)}</>
                ) : null}
              </>
            )}
          </p>
          <p className="text-xs text-ink-500">
            {list.items.length} {list.items.length === 1 ? 'emne' : 'emner'}
          </p>
        </div>

        {!isUncategorized && list.id !== null ? (
          <Menu
            align="right"
            dense
            portal
            trigger={<MoreHorizontal className="h-4 w-4" aria-hidden />}
            triggerAriaLabel="Listehandlinger"
            triggerClassName="p-1.5 border border-rule rounded-sm hover:bg-paper-200/60"
          >
            {(close) => (
              <MenuItem
                danger
                onClick={() => {
                  close()
                  setShowDeleteList(true)
                }}
              >
                Slet liste
              </MenuItem>
            )}
          </Menu>
        ) : null}
      </div>

      <section className={sectionClass}>
        {list.items.length === 0 ? (
          <p className="text-xs text-ink-400">Ingen emner i denne liste.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead>
                <tr className="text-ink-500 uppercase tracking-wide">
                  <th className="py-1.5 pr-3 font-medium">Emne</th>
                  <th className="py-1.5 pr-3 font-medium">Type</th>
                  <th className="py-1.5 pr-3 font-medium">ID</th>
                  <th className="py-1.5 pr-3 font-medium">Tilføjet</th>
                  <th className="py-1.5 font-medium w-8">
                    <span className="sr-only">Handlinger</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule text-ink">
                {list.items.map((item) => (
                  <tr key={item.id}>
                    <td className="py-1.5 pr-3 max-w-[12rem]">
                      <EntityProfileLink
                        to={entityProfilePath(item)}
                        entityType={item.entityType}
                        entityId={item.entityId}
                        className="truncate block hover:underline"
                        title={item.displayName}
                        showWatchlist={false}
                      >
                        {item.displayName}
                      </EntityProfileLink>
                    </td>
                    <td className="py-1.5 pr-3 text-ink-500">
                      {item.entityType === 'company' ? 'Virksomhed' : 'Person'}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">{item.entityId}</td>
                    <td className="py-1.5 pr-3 tabular-nums text-ink-500">
                      {formatDateTimeDa(item.createdAt) ?? '—'}
                    </td>
                    <td className="py-1.5">
                      <Menu
                        align="right"
                        dense
                        portal
                        trigger={<MoreHorizontal className="h-3.5 w-3.5" aria-hidden />}
                        triggerAriaLabel={`Handlinger for ${item.displayName}`}
                        triggerClassName="p-1 text-ink-500 hover:text-ink"
                      >
                        {(close) => (
                          <>
                            <MenuItem
                              onClick={() => {
                                close()
                                openEditItem(item)
                              }}
                            >
                              Rediger lister
                            </MenuItem>
                            <MenuSeparator />
                            <MenuItem
                              danger
                              onClick={() => {
                                close()
                                void handleRemoveItem(item)
                              }}
                            >
                              Fjern fra watchlist
                            </MenuItem>
                          </>
                        )}
                      </Menu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        open={editItem !== null}
        onClose={() => setEditItem(null)}
        title="Rediger lister"
        footer={
          <>
            <button
              type="button"
              onClick={() => setEditItem(null)}
              className="px-3 py-1.5 text-xs text-ink-500"
            >
              Annuller
            </button>
            <button
              type="button"
              disabled={itemBusy}
              onClick={() => void saveEditItem()}
              className="px-3 py-1.5 bg-ink text-paper text-xs rounded-sm disabled:opacity-50"
            >
              {itemBusy ? 'Gemmer…' : 'Gem'}
            </button>
          </>
        }
      >
        {editItem ? (
          <>
            <p className="text-xs text-ink-500 mb-3 truncate" title={editItem.displayName}>
              {editItem.displayName}
            </p>
            <WatchlistListPicker
              lists={pickerLists}
              selectedListIds={editListIds}
              onChange={setEditListIds}
              idPrefix="admin-wl"
            />
          </>
        ) : null}
      </Modal>

      <Modal
        open={showDeleteList}
        onClose={() => setShowDeleteList(false)}
        title="Slet liste?"
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => setShowDeleteList(false)}
              className="px-3 py-1.5 text-sm text-ink-500"
            >
              Annuller
            </button>
            <button
              type="button"
              disabled={deleteListBusy}
              onClick={() => void handleDeleteList()}
              className="px-3 py-1.5 bg-signal text-paper text-sm rounded-sm disabled:opacity-50"
            >
              {deleteListBusy ? 'Sletter…' : 'Slet liste'}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink-500">
          Listen <strong className="text-ink">{list.name}</strong> slettes. Emnerne forbliver på
          watchlist (uden liste), medmindre de kun var i denne liste.
        </p>
      </Modal>
    </div>
  )
}

function BackLink({ userId, userName }: { userId: number; userName: string | null }) {
  return (
    <Link
      to={`/account/admin/brugere/${userId}`}
      className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink mb-4"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      {userName ? userName : 'Bruger'}
    </Link>
  )
}

export default AdminUserWatchlistListPage
